import React from "react";
import Cookies from 'js-cookie';
import { useNavigate } from 'react-router-dom';

import Topbar from "../../components/topbar";
import { Box, Button, Paper, TextField, Typography } from "@mui/material";
import LoadingOverlay from "../../components/loading-overlay";
import { useUser } from "../../providers/user-provider";
import { useToast } from "../../providers/toaster-provider";

import useApi from "../../hooks/use-api";
import buildApiRequestConfig from "../../api/url-builder";
import routes from "../../api/routes";

const AdminImpersonate: React.FC = () => {
    const navigate = useNavigate();
    const { refreshUser } = useUser();
    const { showError, showSuccess } = useToast();
    const [userGuid, setUserGuid] = React.useState("");
    const [loading, setLoading] = React.useState(false);
    const [unlocking, setUnlocking] = React.useState(false);
    const [start, setStart] = useApi();
    const [unlock, setUnlock] = useApi();

    React.useEffect(() => {
        const { data, isLoading, error } = start;
        if (data && !isLoading) {
            // useApi re-sends its last request when the token changes - clear
            // it first, or it goes out again as the impersonated user (not an
            // admin) and fails with "Only admins can impersonate users".
            setStart(null);
            Cookies.set('token', data.token);
            Cookies.set('user', JSON.stringify(data.user));
            Cookies.set('entities', JSON.stringify(data.entities || []));
            // A full load into the user portal, not an in-app navigate - the
            // identity just changed, and anything still holding the admin's
            // state (open requests, AuthGuard's timer on this /admin page)
            // could otherwise act on the new cookies and bounce to a login page.
            window.location.assign('/transaction');
        }
        if (error) {
            showError(error ? error.toString() : 'Failed to start impersonation');
            setLoading(false);
        }
    }, [start, navigate, refreshUser, showError, showSuccess]);

    const handleImpersonate = () => {
        const { url, method } = buildApiRequestConfig(routes.impersonate.start);
        setStart({ url, method, body: { targetUserGuid: userGuid } });
        setLoading(true);
    };

    React.useEffect(() => {
        const { data, isLoading, error } = unlock;
        if (data && !isLoading) {
            showSuccess(data.message || 'Account unlocked');
            setUnlocking(false);
        }
        if (error) {
            showError(error ? error.toString() : 'Failed to unlock account');
            setUnlocking(false);
        }
    }, [unlock, showError, showSuccess]);

    const handleUnlock = () => {
        const { url, method } = buildApiRequestConfig(routes.impersonate.unlock);
        setUnlock({ url, method, body: { targetUserGuid: userGuid } });
        setUnlocking(true);
    };

    return (
        <div style={{ width: '100%' }}>
            <LoadingOverlay 
                isLoading={loading} 
                title="Starting Impersonation..." 
                message="Please wait while we switch to the user account." 
            />
            <Topbar title="Impersonate User"/>
            <div style={{ padding: '10px', background: 'var(--color-page-background)', height: 'calc(100vh - 86px)' }}>
                <Paper sx={{ p: 3 }}>
                    <Box display="flex" flexDirection="column" gap={3} maxWidth={600}>
                        <Box>
                            <Typography fontWeight={500} fontSize={14} mb={0.5}>
                                User GUID
                            </Typography>
                            <TextField 
                                placeholder="Enter user GUID" 
                                fullWidth 
                                size="small"
                                value={userGuid}
                                onChange={(e) => setUserGuid(e.target.value)}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter' && userGuid) {
                                        handleImpersonate();
                                    }
                                }}
                            />
                        </Box>
                        <Box display="flex" gap={2}>
                            <Button
                                variant="contained"
                                sx={{
                                    bgcolor: 'var(--color-primary)',
                                    color: '#fff',
                                    borderRadius: 2,
                                    px: 4,
                                    fontWeight: 600,
                                    textTransform: 'none'
                                }}
                                onClick={handleImpersonate}
                                disabled={!userGuid || loading}
                            >
                                Start Impersonate
                            </Button>
                            <Button
                                variant="outlined"
                                sx={{
                                    borderColor: 'var(--color-primary)',
                                    color: 'var(--color-primary)',
                                    borderRadius: 2,
                                    px: 4,
                                    fontWeight: 600,
                                    textTransform: 'none'
                                }}
                                onClick={handleUnlock}
                                disabled={!userGuid || unlocking}
                            >
                                {unlocking ? 'Unlocking...' : 'Unlock Account'}
                            </Button>
                        </Box>
                    </Box>
                </Paper>
            </div>
        </div>
    );
};

export default AdminImpersonate;
